import React, { useEffect, useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Chip,
  TextField,
  Alert,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../api/client';
import { actionBtnSx } from '../styles/uiTokens';

// 설정 키별 메타 정보 (표시 이름, 단위, 타입)
const settingsMeta = {
  refund_cutoff_days: {
    label: '환불 불가 기준일',
    description: '이 일수 이후는 환불 불가, 해지만 가능',
    unit: '일',
    type: 'number',
  },
  refund_penalty_rate_early: {
    label: '0~7일 위약금 비율',
    description: '결제일로부터 0~7일 이내 환불 시 부과되는 위약금 비율',
    unit: '%',
    type: 'percent',
  },
  refund_penalty_rate_late: {
    label: '8~14일 위약금 비율',
    description: '결제일로부터 8~14일 이내 환불 시 부과되는 위약금 비율',
    unit: '%',
    type: 'percent',
  },
  refund_per_wash_price: {
    label: '1회 차감액',
    description: '환불 시 세차 1회당 차감되는 금액',
    unit: '원',
    type: 'number',
  },
};

// 정책 값은 상태나 행동이 아니라 표 안의 데이터다. 강조색 Chip 대신 숫자를
// 안정적으로 읽을 수 있는 일반 텍스트로 표현해 설정 값끼리 같은 무게를 유지한다.
const policyValueSx = {
  color: 'text.primary',
  fontSize: 14,
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 700,
  lineHeight: 1.4,
};

const SettingsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/settings');
      if (res.data.success) {
        setSettings(res.data.settings || []);
      }
    } catch (err) {
      console.error('설정 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingKey(setting.key);
    const meta = settingsMeta[setting.key];
    if (meta?.type === 'percent') {
      setEditValue((setting.value * 100).toString());
    } else {
      setEditValue(setting.value.toString());
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleSave = async (key) => {
    const meta = settingsMeta[key];
    let valueToSave = parseFloat(editValue);

    if (meta?.type === 'percent') {
      valueToSave = valueToSave / 100;
    }

    try {
      await api.put(`/settings/${key}`, {
        value: valueToSave,
        description: meta?.description,
      });
      alert('설정이 저장되었습니다.');
      setEditingKey(null);
      fetchSettings();
    } catch (err) {
      alert('저장에 실패했습니다.');
    }
  };

  const formatValue = (setting) => {
    const meta = settingsMeta[setting.key];
    if (meta?.type === 'percent') {
      return `${(setting.value * 100).toFixed(0)}%`;
    } else if (meta?.unit === '원') {
      return `${setting.value.toLocaleString('ko-KR')}원`;
    } else if (meta?.unit === '일') {
      return `${setting.value}일`;
    }
    return setting.value;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>
          판매정책 관리
        </Typography>
        <Chip label="환불/정기결제 정책 설정" variant="outlined" color="primary" size="small" />
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        ⚠️ 설정 변경 시 <strong>다음 환불 요청부터 즉시 반영</strong>됩니다. 기존 구독에는 영향을 주지 않으며, 프론트엔드 앱의 안내 문구도 자동으로 업데이트됩니다.
      </Alert>

      {/* 모바일: 카드 리스트 */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Paper sx={{ p: 3, textAlign: 'center', color: '#888' }}>로딩 중...</Paper>
          ) : settings.map((setting) => {
            const meta = settingsMeta[setting.key] || {};
            const isEditing = editingKey === setting.key;
            return (
              <Paper key={setting.key} elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: '12px' }}>
                <Typography variant="body1" fontWeight={700} sx={{ mb: 0.5 }}>
                  {meta.label || setting.key}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.5 }}>
                  {meta.description || setting.description || '-'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                  {isEditing ? (
                    <>
                      <TextField
                        type="number"
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        sx={{ width: 140 }}
                        slotProps={{ input: {
                          endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>{meta.type === 'percent' ? '%' : meta.unit}</Typography>,
                        } }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.75 }}>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={() => handleSave(setting.key)} sx={actionBtnSx}>저장</Button>
                        <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} sx={actionBtnSx}>취소</Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Typography sx={policyValueSx}>{formatValue(setting)}</Typography>
                      <Button variant="outlined" startIcon={<EditIcon />} onClick={() => handleEdit(setting)} sx={actionBtnSx}>수정</Button>
                    </>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Stack>
      ) : (
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
              <TableCell width="20%" sx={{ fontWeight: 700, color: '#1976d2' }}>설정 항목</TableCell>
              <TableCell width="35%" sx={{ fontWeight: 700, color: '#1976d2' }}>설명</TableCell>
              <TableCell width="20%" align="center" sx={{ fontWeight: 700, color: '#1976d2' }}>현재 값</TableCell>
              <TableCell width="15%" align="center" sx={{ fontWeight: 700, color: '#1976d2' }}>작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>로딩 중...</TableCell>
              </TableRow>
            ) : settings.map((setting) => {
              const meta = settingsMeta[setting.key] || {};
              const isEditing = editingKey === setting.key;

              return (
                <TableRow key={setting.key}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {meta.label || setting.key}
                  </TableCell>
                  <TableCell sx={{ color: '#666' }}>
                    {meta.description || setting.description || '-'}
                  </TableCell>
                  <TableCell align="center">
                    {isEditing ? (
                      <TextField
                        type="number"
                        size="small"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        sx={{ width: 120 }}
                        slotProps={{ input: {
                          endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>{meta.type === 'percent' ? '%' : meta.unit}</Typography>,
                        } }}
                      />
                    ) : (
                      <Typography sx={policyValueSx}>{formatValue(setting)}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {isEditing ? (
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSave(setting.key)}
                        >
                          저장
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={handleCancel}
                        >
                          취소
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(setting)}
                      >
                        수정
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          💡 참고사항
        </Typography>
        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }} elevation={0}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>1회 차감액:</strong> 환불 시 세차 이용 횟수당 차감되는 금액입니다. 예를 들어 16,000원으로 설정하고 사용자가 세차를 2회 이용했다면, 환불 시 32,000원이 차감됩니다.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>위약금 비율:</strong> 결제일로부터 경과한 일수에 따라 부과되는 위약금 비율입니다. 0~7일은 조기 환불, 8~14일은 후기 환불로 구분됩니다.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • <strong>환불 불가 기준일:</strong> 기본 15일로 설정되어 있으며, 이 일수 이후에는 환불이 불가능하고 해지만 가능합니다.
          </Typography>
          <Typography variant="body2" color="error">
            ⚠️ 설정 변경은 즉시 반영되므로 신중하게 수정해주세요.
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default SettingsPage;
